import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Gift } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import GiftPersonalisationModal from "@/components/GiftPersonalisationModal";
import SignatureBoxKeepsakeSelector from "@/components/SignatureBoxKeepsakeSelector";

interface KeepsakeSelection {
  id: string;
  name: string;
  image_url: string | null;
}

interface GiftProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string | null;
  category: string | null;
  tags: string[] | null;
  image_url: string | null;
  reason?: string;
}

interface GiftBrowserProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId?: string;
  contactName?: string;
  occasionType?: string;
  generatedMessage?: string;
}

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "signature_box", label: "Signature Box" },
  { key: "keepsake", label: "Keepsakes" },
  { key: "greeting_card", label: "Cards" },
];

const GiftBrowser = ({
  open,
  onOpenChange,
  contactId,
  contactName,
  occasionType,
  generatedMessage = "",
}: GiftBrowserProps) => {
  const isMobile = useIsMobile();
  const [suggestions, setSuggestions] = useState<GiftProduct[]>([]);
  const [allProducts, setAllProducts] = useState<GiftProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState<GiftProduct | null>(null);
  const [personalisationOpen, setPersonalisationOpen] = useState(false);
  const [keepsakeSelectorOpen, setKeepsakeSelectorOpen] = useState(false);
  const [selectedKeepsake, setSelectedKeepsake] = useState<KeepsakeSelection | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSuggestions([]);
    setAllProducts([]);
    setActiveCategory("all");

    const fetchGifts = async () => {
      try {
        // If no contactId, skip AI suggestions and show full catalogue
        if (!contactId) {
          const { data: products } = await supabase
            .from("gift_products")
            .select("id, name, description, price, currency, category, tags, image_url")
            .eq("available", true)
            .order("price", { ascending: true });
          setSuggestions([]);
          setAllProducts((products as GiftProduct[]) ?? []);
          setLoading(false);
          return;
        }

        // Fetch contact to check if they have About data
        const { data: contactData } = await supabase
          .from("contacts")
          .select("personality, loves, dislikes, gift_ideas, shared_memories")
          .eq("id", contactId)
          .maybeSingle();

        const hasAboutData = contactData?.personality ||
          (contactData?.loves && (contactData.loves as string[]).length > 0) ||
          (contactData?.dislikes && (contactData.dislikes as string[]).length > 0) ||
          contactData?.gift_ideas ||
          contactData?.shared_memories;

        if (!hasAboutData) {
          // No About data — skip AI suggestions, show full catalogue directly
          const { data: products } = await supabase
            .from("gift_products")
            .select("id, name, description, price, currency, category, tags, image_url")
            .eq("available", true)
            .order("price", { ascending: true });
          setSuggestions([]);
          setAllProducts((products as GiftProduct[]) ?? []);
          setLoading(false);
          return;
        }

        const { data, error } = await supabase.functions.invoke("suggest-gifts", {
          body: { contactId, occasionType },
        });

        if (error) {
          console.error("suggest-gifts error:", error);
          const { data: products } = await supabase
            .from("gift_products")
            .select("id, name, description, price, currency, category, tags, image_url")
            .eq("available", true)
            .order("price", { ascending: true });
          setAllProducts((products as GiftProduct[]) ?? []);
        } else {
          setSuggestions(data?.suggestions ?? []);
          setAllProducts(data?.allProducts ?? []);
        }
      } catch {
        const { data: products } = await supabase
          .from("gift_products")
          .select("id, name, description, price, currency, category, tags, image_url")
          .eq("available", true)
          .order("price", { ascending: true });
        setAllProducts((products as GiftProduct[]) ?? []);
      } finally {
        setLoading(false);
      }
    };

    fetchGifts();
  }, [open, contactId, occasionType]);

  const handleSelectProduct = (product: GiftProduct) => {
    setSelectedProduct(product);
    if (product.category === "signature_box") {
      setSelectedKeepsake(null);
      setKeepsakeSelectorOpen(true);
    } else {
      setPersonalisationOpen(true);
    }
  };

  const handleKeepsakeSelected = (keepsake: { id: string; name: string; image_url: string | null }) => {
    setSelectedKeepsake(keepsake);
    setPersonalisationOpen(true);
  };

  const filteredProducts =
    activeCategory === "all"
      ? allProducts
      : allProducts.filter((p) => p.category === activeCategory);

  const firstName = contactName ? contactName.split(" ")[0] : "";

  const content = (
    <div className="space-y-6 pb-6">
      {/* AI Suggestions — only show when opened for a specific contact */}
      {firstName && loading ? (
        <div>
          <p className="text-eyebrow text-accent mb-3">
            Suggested for {firstName}
          </p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-52 shrink-0 space-y-2">
                <Skeleton className="h-40 w-full rounded-2xl" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      ) : suggestions.length > 0 ? (
        <div>
          <p className="text-eyebrow text-accent mb-3">
            Suggested for {firstName}
          </p>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {suggestions.map((product) => (
              <div
                key={product.id}
                className="card-premium w-52 shrink-0 p-3"
              >
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="mb-3 h-36 w-full rounded-2xl object-cover"
                  />
                ) : (
                  <div className="mb-3 flex h-36 w-full items-center justify-center rounded-2xl bg-accent/10">
                    <Gift className="h-8 w-8 text-accent" />
                  </div>
                )}
                <p className="text-card-heading text-foreground leading-tight">
                  {product.name}
                </p>
                {product.reason && (
                  <p className="mt-1 text-xs italic text-muted-foreground leading-snug">
                    {product.reason}
                  </p>
                )}
                <Button
                  onClick={() => handleSelectProduct(product)}
                  className="mt-3 w-full text-xs"
                  size="sm"
                >
                  Send this →
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setActiveCategory(cat.key)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              activeCategory === cat.key
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Product grid */}
      {loading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="aspect-square w-full rounded-2xl" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : filteredProducts.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {filteredProducts.map((product) => (
            <div key={product.id} className="card-premium p-2.5">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="aspect-square w-full rounded-2xl object-cover"
                />
              ) : (
                <div className="flex aspect-square w-full items-center justify-center rounded-2xl bg-accent/10">
                  <Gift className="h-8 w-8 text-accent" />
                </div>
              )}
              <p className="mt-2 text-card-heading text-foreground leading-tight">
                {product.name}
              </p>
              {product.category !== "signature_box" && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  USD {Number(product.price).toFixed(2)}
                </p>
              )}
              <Button
                onClick={() => handleSelectProduct(product)}
                className="mt-2 w-full text-xs"
                size="sm"
              >
                Send →
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-12 text-center">
          <Gift className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-body text-muted-foreground">More gifts coming soon.</p>
        </div>
      )}
    </div>
  );

  const browser = isMobile ? (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="text-left px-4">
          <DrawerTitle className="font-serif text-xl">Send a gift</DrawerTitle>
          <p className="text-sm text-muted-foreground">Curated and delivered for you</p>
        </DrawerHeader>
        <div className="overflow-y-auto flex-1 px-4">
          {content}
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Send a gift</DialogTitle>
          <p className="text-sm text-muted-foreground">Curated and delivered for you</p>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {browser}
      {selectedProduct && (
        <>
          <SignatureBoxKeepsakeSelector
            open={keepsakeSelectorOpen}
            onOpenChange={setKeepsakeSelectorOpen}
            onSelect={handleKeepsakeSelected}
          />
          <GiftPersonalisationModal
            open={personalisationOpen}
            onOpenChange={(v) => {
              setPersonalisationOpen(v);
              if (!v) {
                setSelectedProduct(null);
                setSelectedKeepsake(null);
              }
            }}
            contactName={contactName}
            contactId={contactId}
            generatedMessage={generatedMessage}
            occasionType={occasionType}
            productId={selectedProduct.id}
            productName={selectedProduct.name}
            productPrice={`USD ${Number(selectedProduct.price).toFixed(2)}`}
            productImageUrl={selectedProduct.image_url ?? undefined}
            productCategory={selectedProduct.category ?? undefined}
            selectedKeepsake={selectedKeepsake ?? undefined}
          />
        </>
      )}
    </>
  );
};

export default GiftBrowser;
